import isFunction from "lodash/isFunction";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { Icon } from "@/react/components/shared/Icon";
import { TEMPLATE_ICONS } from "@/react/components/shared/Workflows/mapTemplateIcon";
import { Typography } from "@/react/components/shared/uikit/Typography";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { workflowsSettingsPaths } from "@/react/helpers/urlHelpers/workflowsSettingsPaths";
import type { Template } from "../../../generated/WorkflowsApi.types";
import { useDeriveCategoryAndLabelsFromCategories } from "../../../hooks/useDeriveCategoryAndLabelsFromCategories";
import { AIActionLabel } from "../../Labels/AIActionLabel";
import { EmailHubLabel } from "../../Labels/EmailHubLabel";
import { NewLabel } from "../../Labels/NewLabel";
import { WorkflowTemplateCategoryPill } from "../../WorkflowTemplateCategoryPill";
import { WorkflowTemplateTypePill } from "../../WorkflowTemplateTypePill";

type TemplateType = Template["template_type"];

const localeNamespace = "workflows.templates";
export interface WorkflowTemplateCardProps {
  categories?: string[];
  displayLabels?: boolean;
  id: string;
  isNew?: boolean;
  onClose?: () => void;
  templateKey: string;
  templateType: TemplateType;
}

export function WorkflowTemplateCard({
  id,
  templateType,
  templateKey,
  categories = [],
  isNew = false,
  displayLabels = true,
  onClose,
}: WorkflowTemplateCardProps) {
  const { category, isEmailHub, isAiAgent } =
    useDeriveCategoryAndLabelsFromCategories(categories);
  const history = useHistory();

  const onClick = () => {
    onClose?.();
    history.push(workflowsSettingsPaths.preview({ workflowId: id }));
  };

  const hasOnClick = isFunction(onClick);

  const clickProps = hasOnClick
    ? {
        role: "button",
        onClick,
      }
    : {};

  return (
    <div
      className={classNames(
        "bg-primary border-primary hover:border-dark dark:hover:border-light flex w-80 cursor-pointer flex-col gap-4 rounded-xl border p-4 transition-shadow hover:shadow-md",
        {
          "cursor-pointer": hasOnClick,
        },
      )}
      {...clickProps}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex size-8 items-center justify-center rounded-lg bg-black text-white dark:bg-white dark:text-black">
            <Icon type={TEMPLATE_ICONS[templateKey]} size={16} />
          </div>

          {displayLabels && (
            <div className="flex items-center gap-2">
              {isEmailHub && <EmailHubLabel />}
              {isAiAgent && <AIActionLabel />}
              {isNew && <NewLabel />}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <span className="line-clamp-1 overflow-hidden text-ellipsis">
            <Typography.LabelSm weight="semibold">
              {t([localeNamespace, templateKey, "title"])}
            </Typography.LabelSm>
          </span>
          <span className="line-clamp-2 overflow-hidden text-ellipsis">
            <Typography.BodyXs>
              {t([localeNamespace, templateKey, "description"])}
            </Typography.BodyXs>
          </span>
        </div>
      </div>

      <div className="mt-auto flex flex-wrap gap-2">
        <WorkflowTemplateTypePill templateType={templateType} />
        <WorkflowTemplateCategoryPill category={category} />
      </div>
    </div>
  );
}
